/**
 * The community at a glance.
 *
 * Numbers a moderator actually acts on, then two small charts drawn as plain
 * divs - a fortnight of signups and which games people are posting. No chart
 * library for sixteen bars.
 */

import { useSession } from '@/stores/session';
import { Icon } from '@/icons/Icon';
import { cn } from '@/lib/cn';
import { Card, CardHead } from '@/components/ui';

export type AdminOverview = {
  totals: {
    users: number; online: number; newToday: number; parties: number;
    partiesAllTime: number; messages: number; banned: number; openReports: number;
  };
  signupsByDay: { day: string; count: number }[];
  topGames: { id: string; count: number }[];
};

export function Community({ admin }: { admin: AdminOverview }) {
  const catalog = useSession((s) => s.catalog);
  const t = admin.totals;

  const tiles: [string, number, string, boolean?][] = [
    ['Accounts', t.users, 'users'],
    ['Online now', t.online, 'checkCirc'],
    ['New today', t.newToday, 'sparkle'],
    ['Open parties', t.parties, 'squads'],
    ['Parties all time', t.partiesAllTime, 'chart'],
    ['Messages', t.messages, 'chat'],
    ['Suspended', t.banned, 'ban', t.banned > 0],
    ['Open reports', t.openReports, 'flag', t.openReports > 0]
  ];

  const peak = Math.max(1, ...admin.signupsByDay.map((d) => d.count));
  const busiest = Math.max(1, ...admin.topGames.map((g) => g.count));

  return (
    <>
      <Card>
        <CardHead title="Community" note="Refreshes every 30 seconds" />
        {/* One card, divided - the same shape the badge stats use, so the
            two consoles read as the same application. */}
        <div className="grid divide-x divide-y divide-line-soft overflow-hidden rounded-[calc(12px*var(--radius-mul,1))] border border-line-soft [grid-template-columns:repeat(auto-fit,minmax(190px,1fr))]">
          {tiles.map(([label, value, icon, alert]) => (
            <div
              key={label}
              className={cn(
                'flex items-center gap-3 p-4',
                // Anything needing attention says so rather than sitting in a
                // row of identical grey tiles.
                alert && 'bg-[color-mix(in_srgb,var(--warn)_9%,transparent)]'
              )}
            >
              <span
                className={cn(
                  'grid h-10 w-10 flex-none place-items-center rounded-full border',
                  alert
                    ? 'border-warn bg-[color-mix(in_srgb,var(--warn)_16%,transparent)] text-warn'
                    : 'border-line-soft bg-surface-2 text-accent-hi'
                )}
              >
                <Icon name={icon} size={17} />
              </span>

              <div className="min-w-0">
                <div className="text-[10.5px] font-bold tracking-[.09em] text-mute uppercase">{label}</div>
                <div className={cn('mt-0.5 text-[21px] leading-none font-extrabold tabular-nums', alert && 'text-warn')}>
                  {value.toLocaleString()}
                </div>
              </div>
            </div>
          ))}
        </div>
      </Card>

      <div className="grid gap-[var(--gap)] lg:grid-cols-2">
        <Card>
          <CardHead title="Signups" note="Last 14 days" />
          {/* items-stretch, not items-end: with items-end the columns shrink
              to their label and the bars inside them have no height to fill,
              which is why the chart drew nothing. */}
          <div className="flex h-[130px] items-stretch gap-1.5">
            {admin.signupsByDay.map((d) => (
              <div key={d.day} className="group flex min-w-0 flex-1 flex-col items-center gap-1.5">
                <div className="flex w-full flex-1 items-end">
                  <div
                    title={d.day + ': ' + d.count}
                    className="w-full rounded-t-[calc(4px*var(--radius-mul,1))] bg-accent transition-[height,opacity] duration-300 group-hover:opacity-80"
                    // A zero day still gets a sliver, so the axis reads as a
                    // timeline rather than a gap.
                    style={{ height: Math.max(3, (d.count / peak) * 100) + '%' }}
                  />
                </div>
                <span className="text-[9px] text-ghost tabular-nums">{d.day.slice(8)}</span>
              </div>
            ))}
          </div>
        </Card>

        <Card>
          <CardHead title="Games" note="Parties posted, all time" />
          {admin.topGames.length ? (
            <div className="flex flex-col gap-2">
              {admin.topGames.map((g) => {
                const meta = catalog?.games.find((c) => c.id === g.id);
                return (
                  <div key={g.id} className="flex items-center gap-2.5">
                    <span className="w-[104px] flex-none truncate text-[12.5px] font-semibold">
                      {meta?.name ?? g.id}
                    </span>
                    <span className="h-2.5 flex-1 overflow-hidden rounded-full bg-surface-3">
                      <i
                        className="block h-full rounded-full transition-[width] duration-500"
                        style={{
                          width: (g.count / busiest) * 100 + '%',
                          background: meta?.color ?? 'var(--accent)'
                        }}
                      />
                    </span>
                    <span className="w-8 flex-none text-right text-[12px] font-bold text-dim tabular-nums">
                      {g.count}
                    </span>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="py-6 text-center text-[13px] text-mute">No parties yet.</p>
          )}
        </Card>
      </div>
    </>
  );
}
